/**
 * Central navigation configuration for the app shell.
 *
 * `NAV_GROUPS` is the visual grouping of primary nav entries.
 * `SUITE_ITEMS` holds the Enterprise Workforce Suite entries (appended
 * only when the add-on is active).
 *
 * Both structures are wired to:
 *   - ROUTE_ROLES  → legacy role-based gating (per-route allowlist)
 *   - ROUTE_MODULE → fine-grained permission-module lookups
 *
 * Split from the AppShell in iter-53 to make future nav edits a
 * localised, low-risk change.
 */
import {
  House, Users, ClipboardText, UsersFour, Stethoscope,
  Receipt, GearSix, Buildings, Circle, ChartBar,
  ChatCircle, ClockCounterClockwise, Package, HandCoins, ShieldCheck, Calculator,
  ShieldStar, CalendarBlank, IdentificationBadge, Briefcase, Sparkle,
  Megaphone,
} from "@phosphor-icons/react";
import { ROUTE_ROLES } from "@/lib/routeRoles";

export const ROUTE_MODULE = {
  "/dashboard": "dashboard",
  "/attendance": "attendance",
  "/appointments": "appointments",
  "/queue": "student_queue",
  "/therapists": "therapist_queue",
  "/sessions": "sessions",
  "/students": "student",
  "/messages": "message",
  "/billing": "invoice",
  "/grants": "grants",
  "/inventory": "inventory",
  "/certifications": "certifications",
  "/reports": "reports",
  "/audit": "logs",
  "/settings": "settings",
  "/workforce/employees": "workforce_admin",
  "/workforce/schedule":  "workforce_admin",
  "/workforce/holidays":  "workforce_admin",
  "/my-approvals":        "my_approvals",
  "/app/publish":         "publish",
};

// Exact wording must match backend + marketing copy.
export const SUITE_LABEL =
  "TherapyStack Pro – Enterprise Workforce Management and Payroll Suite";

const RAW_GROUPS = [
  {
    label: "Workspace",
    items: [
      { to: "/dashboard",  label: "Dashboard",       icon: House },
    ],
  },
  {
    label: "Operations",
    items: [
      { to: "/attendance", label: "Attendance",      icon: ClipboardText },
      { to: "/appointments", label: "Appointments",  icon: CalendarBlank },
      { to: "/queue",      label: "Student Queue",   icon: Circle },
      { to: "/therapists", label: "Therapist Queue", icon: Stethoscope },
      { to: "/sessions",   label: "Sessions",        icon: UsersFour },
    ],
  },
  {
    label: "People",
    items: [
      { to: "/students",     label: "Students",       icon: Users },
      { to: "/workforce/employees", label: "Employees", icon: IdentificationBadge },
      { to: "/messages",     label: "Messages",       icon: ChatCircle },
      { to: "/app/publish",  label: "Publish",        icon: Megaphone },
      { to: "/my-approvals", label: "My Approvals",   icon: ClipboardText },
    ],
  },
  {
    label: "Finance",
    items: [
      { to: "/billing",    label: "Billing",         icon: Receipt },
      { to: "/grants",     label: "Grants",          icon: HandCoins },
    ],
  },
  {
    label: "HR & Compliance",
    items: [
      { to: "/inventory",      label: "Inventory",      icon: Package },
      { to: "/certifications", label: "Certifications", icon: ShieldCheck },
    ],
  },
  {
    label: "Insights",
    items: [
      { to: "/reports",    label: "Reports",         icon: ChartBar },
      { to: "/audit",      label: "Audit Log",       icon: ClockCounterClockwise },
    ],
  },
  {
    label: "System",
    items: [
      { to: "/settings",   label: "Settings",        icon: GearSix },
    ],
  },
];

export const NAV_GROUPS = RAW_GROUPS.map(group => ({
  ...group,
  items: group.items.map(item => ({
    ...item,
    roles: ROUTE_ROLES[item.to] || [],
    module: ROUTE_MODULE[item.to] || null,
  })),
}));

// Enterprise Workforce Suite — appended only when the workspace has the
// add-on active. `hr_rule_manager` is a distinct permission, not a role.
// iter-67d — Employees moved OUT of this section (now under People,
// available to every workspace regardless of add-on subscription).
export const SUITE_ITEMS = [
  { to: "/workforce/schedule",     label: "Schedule",            icon: CalendarBlank,       roles: ROUTE_ROLES["/workforce/schedule"]     || [], module: "workforce_admin" },
  { to: "/workforce/holidays",     label: "Holidays",            icon: Sparkle,             roles: ROUTE_ROLES["/workforce/holidays"]     || [], module: "workforce_admin" },
  { to: "/workforce/salary-rules", label: "Salary Rule Builder", icon: Calculator,          roles: ROUTE_ROLES["/workforce/salary-rules"] || [], module: "hr_rule_manager" },
  { to: "/payroll",                label: "Payroll",             icon: Calculator,          roles: ROUTE_ROLES["/payroll"]                || [], module: null },
];

// Icons re-exported so consumers don't need to know their origin.
export { Briefcase, ShieldStar, Buildings };
